<script lang="ts">
  import { onMount } from 'svelte';
  import type { CreateStaffUserRequest, StaffUserSummary, TenantSummary } from '@ucc/shared';
  import {
    ApiError,
    createStaffUser,
    fetchStaffUsers,
    fetchTenants,
    resetStaffPassword,
    updateStaffUser,
  } from '$lib/api';
  import { session } from '$lib/session.svelte';
  import Card from '$lib/components/Card.svelte';

  let users = $state<StaffUserSummary[]>([]);
  let tenants = $state<TenantSummary[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let actionError = $state<string | null>(null);
  let actionOk = $state<string | null>(null);
  let q = $state('');
  let tenantFilter = $state('all');
  let statusFilter = $state<'all' | 'active' | 'disabled'>('all');
  let busyId = $state<string | null>(null);

  let showCreate = $state(false);
  let creating = $state(false);
  let createEmail = $state('');
  let createDisplayName = $state('');
  let createRole = $state<'admin' | 'user'>('user');
  let createTenantId = $state('');

  const isAdmin = $derived(session.user?.role === 'admin');
  const meId = $derived(session.user?.id ?? '');

  const tenantName = $derived.by(() => {
    const map = new Map(tenants.map((t) => [t.id, t.name]));
    return (id: string | null | undefined) => {
      if (!id) return '—';
      return map.get(id) ?? id.slice(0, 8);
    };
  });

  const filtered = $derived.by(() => {
    const needle = q.trim().toLowerCase();
    return users.filter((u) => {
      if (tenantFilter !== 'all') {
        if (tenantFilter === '__none__') {
          if (u.tenantId) return false;
        } else if (u.tenantId !== tenantFilter) {
          return false;
        }
      }
      const disabled = Boolean(u.disabledAt);
      if (statusFilter === 'active' && disabled) return false;
      if (statusFilter === 'disabled' && !disabled) return false;
      if (!needle) return true;
      const hay = [u.email, u.displayName ?? '', u.role, tenantName(u.tenantId)]
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  });

  const activeCount = $derived(users.filter((u) => !u.disabledAt).length);
  const disabledCount = $derived(users.filter((u) => Boolean(u.disabledAt)).length);

  function roleLabel(role: string): string {
    if (role === 'admin') return 'Admin';
    if (role === 'user') return 'User';
    if (role === 'client') return 'Client';
    return role;
  }

  function fmtWhen(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  }

  async function load() {
    loading = true;
    error = null;
    try {
      const [uRes, tRes] = await Promise.all([
        fetchStaffUsers(),
        fetchTenants().catch(() => ({ tenants: [] as TenantSummary[] })),
      ]);
      users = uRes.users;
      tenants = tRes.tenants;
      if (!createTenantId && tenants[0]) createTenantId = tenants[0].id;
    } catch (err) {
      error = err instanceof ApiError ? err.message : 'Failed to load staff users';
      users = [];
    } finally {
      loading = false;
    }
  }

  function resetCreateForm() {
    createEmail = '';
    createDisplayName = '';
    createRole = 'user';
    createTenantId = tenants[0]?.id ?? '';
  }

  async function onCreate(e: Event) {
    e.preventDefault();
    if (!isAdmin) return;
    actionError = null;
    actionOk = null;
    const email = createEmail.trim().toLowerCase();
    const displayName = createDisplayName.trim() || email;
    const tenantId = createTenantId.trim();
    if (!email) {
      actionError = 'Email is required.';
      return;
    }
    if (!tenantId) {
      actionError = 'Tenant is required.';
      return;
    }
    creating = true;
    try {
      const body: CreateStaffUserRequest = {
        email,
        role: createRole,
        displayName,
        tenantId,
      };
      const res = await createStaffUser(body);
      users = [res.user, ...users.filter((u) => u.id !== res.user.id)];
      actionOk = res.emailSent
        ? `Invite sent to ${res.user.email}. They must activate within 72 hours.`
        : `User created; invite email could not be sent${res.emailError ? ` (${res.emailError})` : ''}.`;
      showCreate = false;
      resetCreateForm();
    } catch (err) {
      actionError = err instanceof ApiError ? err.message : 'Failed to create staff user';
    } finally {
      creating = false;
    }
  }

  async function setDisabled(user: StaffUserSummary, disabled: boolean) {
    if (!isAdmin) return;
    if (user.id === meId) {
      actionError = 'You cannot disable your own account.';
      return;
    }
    actionError = null;
    actionOk = null;
    busyId = user.id;
    try {
      const res = await updateStaffUser(user.id, { disabled });
      users = users.map((u) => (u.id === res.user.id ? res.user : u));
      actionOk = disabled
        ? `Disabled ${res.user.email} (sessions revoked)`
        : `Enabled ${res.user.email}`;
    } catch (err) {
      actionError = err instanceof ApiError ? err.message : 'Failed to update staff user';
    } finally {
      busyId = null;
    }
  }

  async function onResetPassword(user: StaffUserSummary) {
    if (!isAdmin) return;
    if (user.id === meId) {
      actionError = 'You cannot reset your own password from here. Use Account settings.';
      return;
    }
    if (user.disabledAt) {
      actionError = 'Enable the account before sending a reset link.';
      return;
    }
    if (
      !window.confirm(
        user.activatedAt
          ? `Send password reset email to ${user.email}?`
          : `Resend activation invite to ${user.email}?`,
      )
    ) {
      return;
    }
    actionError = null;
    actionOk = null;
    busyId = user.id;
    try {
      const res = await resetStaffPassword(user.id);
      actionOk = res.emailSent
        ? user.activatedAt
          ? `Reset email sent to ${user.email}`
          : `Invite re-sent to ${user.email}`
        : `Link created but email failed${res.emailError ? ` (${res.emailError})` : ''}`;
    } catch (err) {
      actionError = err instanceof ApiError ? err.message : 'Failed to send reset email';
    } finally {
      busyId = null;
    }
  }

  onMount(() => {
    void load();
  });
</script>

<div class="page">
  <a class="back" href="#/settings">← Settings</a>
  <header class="head">
    <div>
      <p class="eyebrow">Platform</p>
      <h2>Staff users</h2>
      <p class="sub">
        Directory of portal operators. Admins can create staff and disable accounts (sessions are
        revoked on disable).
      </p>
    </div>
    <div class="head-actions">
      {#if isAdmin}
        <button
          type="button"
          class="primary"
          onclick={() => {
            showCreate = !showCreate;
            actionError = null;
            actionOk = null;
          }}
        >
          {showCreate ? 'Close form' : 'New staff'}
        </button>
      {/if}
      <button type="button" class="refresh" onclick={() => void load()} disabled={loading}>
        {loading ? 'Loading…' : 'Refresh'}
      </button>
    </div>
  </header>

  <div class="kpis">
    <div class="kpi">
      <span class="kpi-label">Staff</span>
      <strong>{users.length}</strong>
    </div>
    <div class="kpi">
      <span class="kpi-label">Active</span>
      <strong>{activeCount}</strong>
    </div>
    <div class="kpi">
      <span class="kpi-label">Disabled</span>
      <strong>{disabledCount}</strong>
    </div>
  </div>

  {#if actionError}
    <p class="flash error" role="alert">{actionError}</p>
  {/if}
  {#if actionOk}
    <p class="flash ok" role="status">{actionOk}</p>
  {/if}

  {#if isAdmin && showCreate}
    <Card title="Create staff user" subtitle="Admin only — sends a 72-hour activation invite">
      <form class="create" onsubmit={(e) => void onCreate(e)}>
        <label class="field">
          <span>Email</span>
          <input type="email" required autocomplete="off" bind:value={createEmail} />
        </label>
        <label class="field">
          <span>Display name</span>
          <input
            type="text"
            autocomplete="off"
            bind:value={createDisplayName}
            placeholder="Optional"
          />
        </label>
<label class="field">
          <span>Role</span>
          <select bind:value={createRole}>
            <option value="admin">Admin</option>
            <option value="user">User</option>
          </select>
        </label>
        <label class="field">
          <span>Tenant</span>
          <select bind:value={createTenantId} required>
            <option value="" disabled={Boolean(createTenantId)}>Select tenant</option>
            {#each tenants as t (t.id)}
              <option value={t.id}>{t.name}</option>
            {/each}
          </select>
        </label>
        <div class="form-actions">
          <button type="submit" class="primary" disabled={creating}>
            {creating ? 'Creating…' : 'Create user'}
          </button>
        </div>
      </form>
    </Card>
  {/if}

  <Card title="Staff directory" subtitle={`${filtered.length} shown`} padded={false}>
    <div class="toolbar">
      <label class="field grow">
        <span class="sr-only">Search</span>
        <input type="search" placeholder="Search email, name, role…" bind:value={q} />
      </label>
      {#if tenants.length > 0}
        <label class="field">
          <span class="sr-only">Tenant</span>
          <select bind:value={tenantFilter}>
            <option value="all">All tenants</option>
            <option value="__none__">No tenant</option>
            {#each tenants as t (t.id)}
              <option value={t.id}>{t.name}</option>
            {/each}
          </select>
        </label>
      {/if}
      <label class="field">
        <span class="sr-only">Status</span>
        <select bind:value={statusFilter}>
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
        </select>
      </label>
    </div>

    {#if error}
      <p class="banner error" role="alert">{error}</p>
    {:else if loading}
      <p class="banner muted">Loading staff…</p>
    {:else if filtered.length === 0}
      <p class="banner muted">No staff users match your filters.</p>
    {:else}
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Tenant</th>
              <th>Status</th>
              <th>Created</th>
              {#if isAdmin}<th>Actions</th>{/if}
            </tr>
          </thead>
          <tbody>
            {#each filtered as u (u.id)}
              {@const disabled = Boolean(u.disabledAt)}
              {@const isSelf = u.id === meId}
              <tr class:disabled-row={disabled}>
                <td class="email">{u.email}</td>
                <td>{u.displayName ?? '—'}</td>
                <td><span class="pill role">{roleLabel(u.role)}</span></td>
                <td>{tenantName(u.tenantId)}</td>
                <td>
                  {#if disabled}
                    <span class="pill bad">Disabled</span>
                  {:else if !u.activatedAt}
                    <span class="pill warn">Pending</span>
                  {:else}
                    <span class="pill ok">Active</span>
                  {/if}
                </td>
                <td class="muted">{fmtWhen(u.createdAt)}</td>
                {#if isAdmin}
                  <td class="actions">
                    {#if isSelf}
                      <span class="muted">You</span>
                    {:else if disabled}
                      <button
                        type="button"
                        class="linkish"
                        disabled={busyId === u.id}
                        onclick={() => void setDisabled(u, false)}
                      >
                        {busyId === u.id ? '…' : 'Enable'}
                      </button>
                    {:else}
                      <button
                        type="button"
                        class="linkish danger"
                        disabled={busyId === u.id}
                        onclick={() => void setDisabled(u, true)}
                      >
                        {busyId === u.id ? '…' : 'Disable'}
                      </button>
                      <button
                        type="button"
                        class="linkish"
                        disabled={busyId === u.id}
                        onclick={() => void onResetPassword(u)}
                      >
                        {busyId === u.id ? '…' : u.activatedAt ? 'Reset password' : 'Resend invite'}
                      </button>
                    {/if}
                  </td>
                {/if}
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </Card>
</div>

<style>
  .page {
    display: grid;
    gap: 1.25rem;
  }

  .head {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
  }
  .back {
    font-size: 0.875rem;
    color: var(--text-muted);
    text-decoration: none;
  }
  .back:hover {
    color: var(--text);
  }

  .head-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .eyebrow {
    margin: 0 0 0.25rem;
    font-size: var(--text-xs, 0.75rem);
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--text-muted);
  }

  h2 {
    margin: 0;
    font-size: var(--text-xl, 1.35rem);
    font-weight: 700;
  }

  .sub {
    margin: 0.35rem 0 0;
    max-width: 40rem;
    color: var(--text-muted);
    font-size: var(--text-sm, 0.9rem);
  }

  .refresh,
  .primary,
  .linkish {
    font: inherit;
    cursor: pointer;
  }

  .refresh {
    border: 1px solid var(--border);
    background: var(--surface);
    color: inherit;
    border-radius: 8px;
    padding: 0.45rem 0.85rem;
  }

  .refresh:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .primary {
    border: 1px solid var(--accent, #2563eb);
    background: var(--accent, #2563eb);
    color: #fff;
    border-radius: 8px;
    padding: 0.45rem 0.85rem;
    font-weight: 600;
  }

  .primary:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  .kpis {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .kpi {
    min-width: 7.5rem;
    padding: 0.75rem 1rem;
    border: 1px solid var(--border);
    border-radius: 10px;
    background: var(--surface);
    display: grid;
    gap: 0.2rem;
  }

  .kpi-label {
    font-size: var(--text-xs, 0.75rem);
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .kpi strong {
    font-size: 1.25rem;
  }

  .flash {
    margin: 0;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    font-size: var(--text-sm, 0.9rem);
  }

  .flash.error {
    background: color-mix(in srgb, var(--danger, #b91c1c) 12%, transparent);
    color: var(--danger, #b91c1c);
  }

  .flash.ok {
    background: color-mix(in srgb, #15803d 12%, transparent);
    color: #15803d;
  }

  .create {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr));
    gap: 0.85rem 1rem;
  }

  .create .field span {
    display: block;
    margin-bottom: 0.3rem;
    font-size: var(--text-xs, 0.75rem);
    font-weight: 600;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .form-actions {
    grid-column: 1 / -1;
    display: flex;
    justify-content: flex-end;
  }

  .toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 0.65rem;
    padding: 0.85rem 1rem;
    border-bottom: 1px solid var(--border);
  }

  .field {
    display: block;
    min-width: 9rem;
  }

  .field.grow {
    flex: 1 1 14rem;
    min-width: 12rem;
  }

  input,
  select {
    width: 100%;
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 0.5rem 0.65rem;
    font: inherit;
    background: var(--surface);
    color: inherit;
  }

  .banner {
    margin: 0;
    padding: 1.25rem 1rem;
    font-size: var(--text-sm, 0.9rem);
  }

  .banner.muted {
    color: var(--text-muted);
  }

  .banner.error {
    color: var(--danger, #b91c1c);
  }

  .table-wrap {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--text-sm, 0.9rem);
  }

  th,
  td {
    text-align: left;
    padding: 0.7rem 1rem;
    border-bottom: 1px solid var(--border);
    vertical-align: middle;
  }

  th {
    font-size: var(--text-xs, 0.75rem);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-muted);
    font-weight: 600;
    background: var(--surface-2, #f9fafb);
    white-space: nowrap;
  }

  tr.disabled-row td {
    opacity: 0.72;
  }

  .email {
    font-weight: 600;
  }

  .muted {
    color: var(--text-muted);
  }

  .pill {
    display: inline-block;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    font-size: var(--text-xs, 0.75rem);
    font-weight: 600;
    background: var(--surface-2, #f3f4f6);
  }

  .pill.ok {
    background: color-mix(in srgb, #15803d 15%, transparent);
    color: #15803d;
  }

  .pill.bad {
    background: color-mix(in srgb, var(--danger, #b91c1c) 15%, transparent);
    color: var(--danger, #b91c1c);
  }

  .pill.warn {
    background: color-mix(in srgb, #d97706 18%, transparent);
    color: #b45309;
  }

  .pill.role {
    text-transform: capitalize;
  }

  .actions {
    white-space: nowrap;
  }

  .linkish {
    border: 0;
    background: transparent;
    color: var(--accent, #2563eb);
    padding: 0.2rem 0.35rem;
    font-weight: 600;
  }

  .linkish.danger {
    color: var(--danger, #b91c1c);
  }

  .linkish:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }
</style>
